/**
 * 流程节点树工具（前端）：解析、扁平化、条件求值、线性有效路径解析。
 * 与后端 WfEngineServiceImpl 的 resolvePath/selectBranch/条件求值逻辑保持一致。
 */

export function parseFlow(flowJson) {
  if (!flowJson) return null
  if (typeof flowJson === 'object') return flowJson
  try { return JSON.parse(flowJson) } catch (e) { return null }
}

export function parseData(json) {
  if (!json) return {}
  if (typeof json === 'object') return json
  try { return JSON.parse(json) || {} } catch (e) { return {} }
}

export function flatten(node) {
  const list = []
  const walk = n => {
    if (!n) return
    list.push(n)
    if (n.branches) n.branches.forEach(b => walk(b.child))
    walk(n.child)
  }
  walk(node)
  return list
}

function isEmpty(v) {
  return v == null || v === '' || (Array.isArray(v) && !v.length)
}
function isNum(v) {
  if (typeof v === 'number') return !isNaN(v)
  return typeof v === 'string' && v.trim() !== '' && !isNaN(Number(v))
}
function eq(a, b) {
  if (isEmpty(a) && isEmpty(b)) return true
  if (isEmpty(a) || isEmpty(b)) return false
  if (isNum(a) && isNum(b)) return Number(a) === Number(b)
  return String(a) === String(b)
}
function cmp(a, b) {
  if (isEmpty(a) && isEmpty(b)) return 0
  if (isEmpty(a)) return -1
  if (isEmpty(b)) return 1
  if (isNum(a) && isNum(b)) return Number(a) - Number(b)
  return String(a).localeCompare(String(b))
}

export function evalRule(r, fd) {
  if (!r || !r.field || !r.op) return false
  const a = fd ? fd[r.field] : undefined
  const b = r.value
  switch (r.op) {
    case 'empty': return isEmpty(a)
    case 'notEmpty': return !isEmpty(a)
    case '=': case '==': case 'eq': return eq(a, b)
    case '!=': case '<>': case 'ne': return !eq(a, b)
    case '>': case 'gt': return cmp(a, b) > 0
    case '>=': case 'gte': return cmp(a, b) >= 0
    case '<': case 'lt': return cmp(a, b) < 0
    case '<=': case 'lte': return cmp(a, b) <= 0
    case 'contains':
      return Array.isArray(a) ? a.some(x => eq(x, b)) : (a == null ? false : String(a).indexOf(String(b)) >= 0)
    case 'in': {
      const arr = Array.isArray(b) ? b : String(b).split(',')
      return arr.some(x => eq(a, x))
    }
    default: return false
  }
}

export function evalCondition(cond, formData) {
  if (!cond) return true
  const rules = cond.rules || []
  // 有条件对象但无规则：视为未配置，不匹配（默认分支用 condition=null 表达）
  if (!rules.length) return false
  const isAnd = (cond.logic || 'and') !== 'or'
  for (const r of rules) {
    const res = evalRule(r, formData)
    if (isAnd && !res) return false
    if (!isAnd && res) return true
  }
  return isAnd
}

function selectBranch(node, formData) {
  const bs = node.branches || []
  let def = null
  for (const b of bs) {
    if (!b.condition) { def = b; continue }
    if (evalCondition(b.condition, formData)) return b
  }
  return def
}

/**
 * 把节点树按表单数据解析为线性有效路径（条件分支确定性展开、含汇聚）。
 */
export function resolvePath(node, formData) {
  const result = []
  let cur = node
  let guard = 0
  while (cur && guard++ < 1000) {
    if (cur.type === 'branch') {
      const matched = selectBranch(cur, formData)
      if (matched && matched.child) result.push(...resolvePath(matched.child, formData))
      cur = cur.child
      continue
    }
    result.push(cur)
    cur = cur.child
  }
  return result
}

export function nodeTitle(node) {
  if (!node) return ''
  if (node.props && node.props.name) return node.props.name
  return node.nodeKey || node.type || ''
}
